import { useState, useEffect } from "react";

import { formatDistanceToNow } from "date-fns";
import { Folder, Tag as TagIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DialogFooterWithShortcuts, CancelButton, SubmitButton } from "@/components/ui/dialog-footer-with-shortcuts";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { SaveIndicator } from "@/components/wizard/SaveIndicator";
import { useFormPersistence } from "@/hooks/useFormPersistence";
import { useGamification } from "@/hooks/useGamification";

interface Board {
  id: string;
  name: string;
  color: string;
}

interface Folder {
  id: string;
  name: string;
  parent_folder_id: string | null;
  depth: number;
}

interface Tag {
  id: string;
  name: string;
  color: string;
}

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boards: Board[];
  folders: Folder[];
  tags: Tag[];
  createProject: (vars: { name: string; board_id: string; folder_id: string | null; tags: string[] }) => void;
  isCreating: boolean;
}

export const CreateProjectDialog = ({ 
  open, 
  onOpenChange, 
  boards, 
  folders, 
  tags, 
  createProject,
  isCreating
}: CreateProjectDialogProps) => {
  const { awardPoints } = useGamification();

  const {
    values: formValues,
    updateValues,
    clearFormData,
    lastSaved,
    hasSavedData,
  } = useFormPersistence(
    {
      projectName: "",
      selectedBoardId: boards[0]?.id || "",
      selectedFolderId: "none",
      selectedTagIds: [] as string[],
    },
    {
      formId: "create-project",
      enabled: open,
    }
  );

  const [projectName, setProjectName] = useState(formValues.projectName);
  const [selectedBoardId, setSelectedBoardId] = useState(formValues.selectedBoardId);
  const [selectedFolderId, setSelectedFolderId] = useState(formValues.selectedFolderId);
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(
    new Set(formValues.selectedTagIds)
  );

  useEffect(() => {
    setProjectName(formValues.projectName);
    setSelectedBoardId(formValues.selectedBoardId || (boards[0]?.id || ""));
    setSelectedFolderId(formValues.selectedFolderId);
    setSelectedTagIds(new Set(formValues.selectedTagIds));
  }, [formValues, boards]);

  const toggleTag = (tagId: string) => {
    const newSelection = new Set(selectedTagIds);
    if (newSelection.has(tagId)) {
      newSelection.delete(tagId);
    } else {
      newSelection.add(tagId);
    }
    setSelectedTagIds(newSelection);
    updateValues({ selectedTagIds: Array.from(newSelection) });
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) {
      toast.error("Please enter a project name");
      return;
    }
    if (!selectedBoardId) {
      toast.error("Please select a board");
      return;
    }

    createProject({
      name: projectName.trim(),
      board_id: selectedBoardId,
      folder_id: selectedFolderId && selectedFolderId !== "none" ? selectedFolderId : null,
      tags: Array.from(selectedTagIds),
    });
    
    awardPoints('project');
    clearFormData();
  };

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
  };

  // Helper to render folder with indentation based on depth
  const renderFolderOption = (folder: Folder) => {
    const indent = "  ".repeat(folder.depth);
    return `${indent}${folder.name}`;
  };

  return (
    <>
      <SaveIndicator lastSaved={lastSaved} />
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          onSubmit={handleCreateProject}
          submitDisabled={isCreating || !projectName.trim() || !selectedBoardId}
        >
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Create New Project</DialogTitle>
                <DialogDescription>
                  Add a new project to one of your boards
                </DialogDescription>
              </div>
              {hasSavedData && lastSaved && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    Draft saved {formatDistanceToNow(lastSaved, { addSuffix: true })}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      clearFormData();
                      toast.success("Draft cleared");
                    }}
                    className="text-muted-foreground"
                  >
                    Clear Draft
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
        
        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-4 py-4 pr-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name</Label>
              <Input
                id="project-name"
                placeholder="Enter project name"
                value={projectName}
                onChange={(e) => {
                  setProjectName(e.target.value);
                  updateValues({ projectName: e.target.value });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && projectName.trim() && selectedBoardId) {
                    handleCreateProject();
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="board-select">Select Board</Label>
              <Select
                value={selectedBoardId}
                onValueChange={(value) => {
                  setSelectedBoardId(value);
                  updateValues({ selectedBoardId: value });
                }}
              >
                <SelectTrigger id="board-select">
                  <SelectValue placeholder="Choose a board" />
                </SelectTrigger>
                <SelectContent>
                  {boards.map((board) => (
                    <SelectItem key={board.id} value={board.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: board.color }}
                        />
                        {board.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {folders.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="folder-select" className="flex items-center gap-2">
                  <Folder className="h-4 w-4" />
                  Folder (Optional)
                </Label>
                <Select
                  value={selectedFolderId}
                  onValueChange={(value) => {
                    setSelectedFolderId(value);
                    updateValues({ selectedFolderId: value });
                  }}
                >
                  <SelectTrigger id="folder-select">
                    <SelectValue placeholder="No folder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No folder</SelectItem>
                    {folders.map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        {renderFolderOption(folder)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {tags.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <TagIcon className="h-4 w-4" />
                  Tags (Optional)
                </Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-md min-h-[42px]">
                  {tags.map((tag) => (
                    <Badge
                      key={tag.id}
                      style={{
                        backgroundColor: selectedTagIds.has(tag.id) ? tag.color : "transparent",
                        color: selectedTagIds.has(tag.id) ? "#ffffff" : tag.color,
                        borderColor: tag.color,
                        borderWidth: "1px",
                        cursor: "pointer",
                      }}
                      className="transition-colors hover:opacity-80"
                      onClick={() => toggleTag(tag.id)}
                    >
                      {tag.name}
                    </Badge>
                  ))}
                </div>
                {selectedTagIds.size > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {selectedTagIds.size} tag{selectedTagIds.size > 1 ? "s" : ""} selected
                  </p>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooterWithShortcuts onSubmit={handleCreateProject}>
          <CancelButton onClick={() => handleOpenChange(false)} disabled={isCreating}>
            Cancel
          </CancelButton>
          <SubmitButton onClick={handleCreateProject} disabled={isCreating || !projectName.trim() || !selectedBoardId}>
            {isCreating ? "Creating..." : "Create Project"}
          </SubmitButton>
        </DialogFooterWithShortcuts>
        </DialogContent>
      </Dialog>
    </>
  );
};
